<template>
  <VContainer>
    <!-- Page Title -->
    <PageTitle />

    <!-- Tabs for API vs Webview -->
    <VRow
      justify="center"
      class="mt-4"
    >
      <VCol
        cols="12"
        md="10"
      >
        <v-tabs
          v-model="activeHomeTab"
          color="primary"
          class="mb-4"
        >
          <v-tab value="api">🔧 Automation by API</v-tab>
          <v-tab value="webview">🌐 Automation by Webview</v-tab>
        </v-tabs>
      </VCol>
    </VRow>

    <v-window v-model="activeHomeTab">
      <!-- API Tab -->
      <v-window-item value="api">
        <!-- Statistics Section -->
        <Statistic
          :totalRuns="totalRuns"
          :successfulRuns="successfulRuns"
          :failedRuns="failedRuns"
        />

        <!-- Automation Buttons -->
        <VRow
          justify="center"
          class="mt-6"
        >
          <VCol
            cols="12"
            md="10"
          >
            <VCard>
              <VCardTitle class="text-h5">Trigger Automation</VCardTitle>
              <VCardText>
                <p>Select an environment to trigger the automation process. Please confirm before proceeding.</p>
                <v-row>
                  <v-col
                    v-for="(env, key) in activateTestCase"
                    :key="key"
                  >
                    <VSelect
                      v-model="env.selected"
                      :items="env.testCases"
                      :item-title="environment => environment.testCases.name"
                      :item-value="environment => environment.testCases.name"
                      :label="env.testCases.length === 0 ? 'There are no test cases' : 'Select Test Case'"
                      outlined
                      dense
                      multiple
                      :loading="isLoadingTestCases"
                      :disabled="isLoadingTestCases"
                    >
                      <template #selection="{ index }">
                        <template v-if="env.testCases.length === 0 && index === 0">
                          <span class="clamp-selection">There are no test cases</span>
                        </template>
                        <template
                          v-else-if="
                            env.selected.length === env.testCases.length && env.testCases.length > 0 && index === 0
                          "
                        >
                          <span class="clamp-selection">ALL</span>
                        </template>
                        <template v-else-if="index === 0">
                          <span class="clamp-selection">{{ env.selected.join(', ') }}</span>
                        </template>
                      </template>
                    </VSelect>
                  </v-col>
                </v-row>
              </VCardText>
              <VCardActions>
                <VRow justify="space-around">
                  <VCol cols="auto">
                    <VBtn
                      variant="tonal"
                      color="primary"
                      prepend-icon="$vuetify"
                      :disabled="isDisabledButton('ballbot')"
                      @click="openConfirmDialog(dynamicEnvironments.ENV_HH_BALLBOT)"
                    >
                      Trigger HH-Ballbot
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      variant="tonal"
                      color="warning"
                      prepend-icon="$vuetify"
                      :disabled="isDisabledButton('venus')"
                      @click="openConfirmDialog(dynamicEnvironments.ENV_HH_VENUS)"
                    >
                      Trigger HH-Venus
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      color="success"
                      variant="tonal"
                      prepend-icon="$vuetify"
                      :disabled="isDisabledButton('production')"
                      @click="openConfirmDialog(dynamicEnvironments.ENV_PRODUCTION)"
                      >Trigger Production
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      color="info"
                      variant="tonal"
                      prepend-icon="$vuetify"
                      :disabled="isDisabledButton('prod-support')"
                      @click="openConfirmDialog(dynamicEnvironments.ENV_PROD_SUPPORT)"
                      >Trigger Prod Support
                    </VBtn>
                  </VCol>
                </VRow>
              </VCardActions>
            </VCard>
          </VCol>
        </VRow>

        <!-- Artifact Statistics Section (API) -->
        <VRow
          justify="center"
          class="mt-10"
        >
          <VCol
            cols="12"
            md="8"
          >
            <VCard
              elevation="4"
              class="rounded-xl"
            >
              <VCardTitle class="text-h5 font-weight-bold text-center py-4 primary--text">
                📊 Recent Artifact Statistics
              </VCardTitle>

              <VDivider></VDivider>
              <template v-if="latestCompletedActivity">
                <VCardText class="text-center py-4">
                  <p class="text-h6 mb-2">
                    Last Completed Activity:
                    <strong>{{ latestCompletedActivity.environment }}</strong>
                  </p>
                  <p class="text-h6 mb-2">
                    Run Number:
                    <a
                      :href="latestCompletedActivity.htmlUrl"
                      target="_blank"
                      rel="noopener"
                    >
                      {{ latestCompletedActivity.runNumber }}
                    </a>
                  </p>
                </VCardText>
              </template>
              <VCardText
                v-if="artifactStatistics"
                class="pt-6 pb-4 px-6"
              >
                <VRow>
                  <VCol
                    cols="12"
                    class="mb-4 text-subtitle-1 font-weight-medium"
                  >
                    <strong>Report Name:</strong> {{ artifactStatistics.reportName }}
                  </VCol>

                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VCard
                      class="pa-4"
                      color="grey lighten-4"
                      flat
                    >
                      <h6 class="text-subtitle-2 font-weight-bold mb-3">🧪 Test Statistics</h6>
                      <VList dense>
                        <VListItem
                          ><strong>Passed:</strong>
                          <span class="text-success">{{ artifactStatistics.statistic.passed }}</span></VListItem
                        >
                        <VListItem
                          ><strong>Failed:</strong>
                          <span class="text-error">{{ artifactStatistics.statistic.failed }}</span></VListItem
                        >
                        <VListItem><strong>Broken:</strong> {{ artifactStatistics.statistic.broken }}</VListItem>
                        <VListItem><strong>Skipped:</strong> {{ artifactStatistics.statistic.skipped }}</VListItem>
                        <VListItem><strong>Total:</strong> {{ artifactStatistics.statistic.total }}</VListItem>
                      </VList>
                    </VCard>
                  </VCol>

                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VCard
                      class="pa-4"
                      color="grey lighten-4"
                      flat
                    >
                      <h6 class="text-subtitle-2 font-weight-bold mb-3">⏱ Execution Time</h6>
                      <VList dense>
                        <VListItem><strong>Start:</strong> {{ formatDate(artifactStatistics.time.start) }}</VListItem>
                        <VListItem><strong>Stop:</strong> {{ formatDate(artifactStatistics.time.stop) }}</VListItem>
                        <VListItem
                          ><strong>Duration:</strong> {{ formatDuration(artifactStatistics.time.duration) }}</VListItem
                        >
                        <VListItem>
                          <v-btn
                            variant="text"
                            @click="
                              openAutomationResult(
                                latestCompletedActivity.runNumber,
                                latestCompletedActivity.environment,
                              )
                            "
                            >See Result
                          </v-btn></VListItem
                        >
                      </VList>
                    </VCard>
                  </VCol>
                </VRow>
              </VCardText>

              <VCardText
                v-else
                class="text-center py-6"
              >
                <p class="text-body-1 mb-2">📭 No artifact statistics available.</p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>

        <!-- Recent Activities Section (API) -->
        <VRow
          justify="center"
          class="mt-10"
        >
          <VCol
            cols="12"
            md="10"
          >
            <VCard>
              <VCardTitle class="text-h5 font-weight-bold"> 🔧 Recent API Automation Activities </VCardTitle>
            </VCard>
          </VCol>
        </VRow>
        <RecentActivities
          :activities="sortedRecentActivities"
          @view-result="openAutomationResult"
        />
      </v-window-item>

      <!-- Webview Tab -->
      <v-window-item value="webview">
        <!-- Webview Statistics Section -->
        <Statistic
          :totalRuns="totalRunsWebview"
          :successfulRuns="successfulRunsWebview"
          :failedRuns="failedRunsWebview"
        />

        <!-- Webview Automation Section -->
        <VRow
          justify="center"
          class="mt-6"
        >
          <VCol
            cols="12"
            md="10"
          >
            <VCard>
              <VCardTitle class="text-h5">Trigger Webview Automation</VCardTitle>
              <VCardText>
                <p>
                  Select an environment to trigger the webview automation process. Please confirm before proceeding.
                </p>
                <v-row>
                  <v-col
                    v-for="(env, key) in activateTestCaseWebview"
                    :key="key"
                  >
                    <VSelect
                      v-model="env.selected"
                      :items="env.testCases"
                      :item-title="environment => environment.testCasesWebview.name"
                      :item-value="environment => environment.testCasesWebview.name"
                      :label="env.testCases.length === 0 ? 'There are no test cases' : 'Select Test Case'"
                      outlined
                      dense
                      multiple
                      :loading="isLoadingTestCasesWebview"
                      :disabled="isLoadingTestCasesWebview"
                    >
                      <template #selection="{ index }">
                        <template v-if="env.testCases.length === 0 && index === 0">
                          <span class="clamp-selection">There are no test cases</span>
                        </template>
                        <template
                          v-else-if="
                            env.selected.length === env.testCases.length && env.testCases.length > 0 && index === 0
                          "
                        >
                          <span class="clamp-selection">ALL</span>
                        </template>
                        <template v-else-if="index === 0">
                          <span class="clamp-selection">{{ env.selected.join(', ') }}</span>
                        </template>
                      </template>
                    </VSelect>
                  </v-col>
                </v-row>
              </VCardText>
              <VCardActions>
                <VRow justify="space-around">
                  <VCol cols="auto">
                    <VBtn
                      variant="tonal"
                      color="primary"
                      prepend-icon="ri-window-line"
                      :disabled="isDisabledButtonWebview('ballbot')"
                      @click="openConfirmDialogWebview(dynamicEnvironments.ENV_HH_BALLBOT)"
                    >
                      Webview HH-Ballbot
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      variant="tonal"
                      color="warning"
                      prepend-icon="ri-window-line"
                      :disabled="isDisabledButtonWebview('venus')"
                      @click="openConfirmDialogWebview(dynamicEnvironments.ENV_HH_VENUS)"
                    >
                      Webview HH-Venus
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      color="success"
                      variant="tonal"
                      prepend-icon="ri-window-line"
                      :disabled="isDisabledButtonWebview('production')"
                      @click="openConfirmDialogWebview(dynamicEnvironments.ENV_PRODUCTION)"
                      >Webview Production
                    </VBtn>
                  </VCol>
                  <VCol cols="auto">
                    <VBtn
                      color="info"
                      variant="tonal"
                      prepend-icon="ri-window-line"
                      :disabled="isDisabledButtonWebview('prod-support')"
                      @click="openConfirmDialogWebview(dynamicEnvironments.ENV_PROD_SUPPORT)"
                      >Webview Prod Support
                    </VBtn>
                  </VCol>
                </VRow>
              </VCardActions>
            </VCard>
          </VCol>
        </VRow>

        <!-- Recent Activities Section (Webview) -->
        <VRow
          justify="center"
          class="mt-10"
        >
          <VCol
            cols="12"
            md="10"
          >
            <VCard>
              <VCardTitle class="text-h5 font-weight-bold"> 🌐 Recent Webview Automation Activities </VCardTitle>
            </VCard>
          </VCol>
        </VRow>
        <RecentActivities
          :activities="sortedRecentActivitiesWebview"
          @view-result="openAutomationResult"
        />
      </v-window-item>
    </v-window>

    <!-- Confirmation Dialog -->
    <VDialog
      v-model="isConfirmDialogActive"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Confirm Automation</VCardTitle>
        <VCardText>
          Are you sure you want to trigger the automation process for the
          <strong>{{ selectedEnvironment }}</strong> environment?
        </VCardText>
        <VCardActions>
          <VBtn
            color="secondary"
            variant="outlined"
            @click="closeConfirmDialog"
            >Cancel</VBtn
          >
          <VBtn
            color="error"
            @click="executeAutomation"
            >Confirm</VBtn
          >
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Webview Confirmation Dialog -->
    <VDialog
      v-model="isConfirmDialogActiveWebview"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Confirm Webview Automation</VCardTitle>
        <VCardText>
          Are you sure you want to trigger the webview automation process for the
          <strong>{{ selectedEnvironmentWebview }}</strong> environment?
        </VCardText>
        <VCardActions>
          <VBtn
            color="secondary"
            variant="outlined"
            @click="closeConfirmDialogWebview"
            >Cancel</VBtn
          >
          <VBtn
            color="error"
            @click="executeAutomationWebview"
            >Confirm</VBtn
          >
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Snackbar for Notifications -->
    <VSnackbar
      v-model="isSnackbarActive"
      :color="snackbarColor"
      multi-line
      dismissible
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VContainer>
</template>
<script setup lang="ts">
/* ==========================
   Imports
========================== */
import downloadArtifactFromApi from '@/api/github_action/downloadArtifact'
import getArtifact from '@/api/github_action/getArtifact'
import PageTitle from '@/components/pages/home/PageTitle.vue'
import RecentActivities from '@/components/pages/home/RecentActivities.vue'
import Statistic from '@/components/pages/home/Statistic.vue'
import { useApiAutomation } from '@/composables/useApiAutomation'
import { useSnackbar } from '@/composables/useSnackbar'
import { useWebviewAutomation } from '@/composables/useWebviewAutomation'
import { useConfigStore } from '@/stores/config'
import { onMounted, ref, watch } from 'vue'

/* ==========================
   Composables
========================== */
const configStore = useConfigStore()
const { isSnackbarActive, snackbarMessage, snackbarColor, showSnackbar, handleError } = useSnackbar()

// API Automation composable
const {
  isConfirmDialogActive,
  selectedEnvironment,
  isLoadingTestCases,
  totalRuns,
  successfulRuns,
  failedRuns,
  activateTestCase,
  dynamicEnvironments,
  sortedRecentActivities,
  latestCompletedActivity,
  isDisabledButton,
  openConfirmDialog,
  closeConfirmDialog,
  fetchWorkflowRuns,
  fetchAllTestCases,
  executeAutomation,
} = useApiAutomation()

// Webview Automation composable (with aliased names)
const webviewAutomation = useWebviewAutomation()
const isConfirmDialogActiveWebview = webviewAutomation.isConfirmDialogActive
const selectedEnvironmentWebview = webviewAutomation.selectedEnvironment
const isLoadingTestCasesWebview = webviewAutomation.isLoadingTestCases
const totalRunsWebview = webviewAutomation.totalRuns
const successfulRunsWebview = webviewAutomation.successfulRuns
const failedRunsWebview = webviewAutomation.failedRuns
const activateTestCaseWebview = webviewAutomation.activateTestCase
const sortedRecentActivitiesWebview = webviewAutomation.sortedRecentActivities
const isDisabledButtonWebview = webviewAutomation.isDisabledButton
const openConfirmDialogWebview = webviewAutomation.openConfirmDialog
const closeConfirmDialogWebview = webviewAutomation.closeConfirmDialog
const fetchWorkflowRunsWebview = webviewAutomation.fetchWorkflowRuns
const fetchAllTestCasesWebview = webviewAutomation.fetchAllTestCases
const executeAutomationWebview = webviewAutomation.executeAutomation

/* ==========================
   Local State
========================== */
const activeHomeTab = ref('api')
const artifactStatistics = ref<any | null>(null)
const artifactZippedId = ref<string | null>('')

/* ==========================
   Watchers
========================== */
watch(artifactZippedId, async newId => {
  if (newId) {
    await downloadArtifact(newId)
  }
})

/* ==========================
   Helper Functions
========================== */
const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

const formatDuration = (duration: number) => {
  const seconds = Math.floor((duration / 1000) % 60)
  const minutes = Math.floor((duration / (1000 * 60)) % 60)
  return `${minutes}m ${seconds}s`
}

/* ==========================
   Artifact Functions
========================== */
const downloadArtifact = async (artifactId: string) => {
  try {
    const result = await downloadArtifactFromApi(artifactId)
    if (result.success) {
      artifactStatistics.value = result.data
    } else {
      throw new Error(result.message)
    }
  } catch (error: any) {
    showSnackbar(`Error: ${error.message}`, 'error')
  }
}

const fetchArtifact = async (runId: string) => {
  try {
    const response = await getArtifact(runId)
    if (response.success) {
      const { artifacts } = response.data
      const artifact = artifacts.find((artifact: any) => artifact.name.includes('zipped'))
      artifactZippedId.value = artifact ? artifact.id : null
    } else {
      throw new Error(response.message)
    }
  } catch (error: any) {
    handleError(error)
  }
}

const fetchAndProcessLatestArtifact = async () => {
  if (latestCompletedActivity.value && latestCompletedActivity.value.id) {
    await fetchArtifact(latestCompletedActivity.value.id)
  }
}

const openAutomationResult = (runNumber: number, environment: string) => {
  if (!environment) {
    console.error(`Invalid environment: ${environment}`)
    return
  }
  let envPath = environment
  if (environment === 'production') {
    envPath = 'prod'
  }
  const url = `https://allure.hh-ballbot.my.id/api/hh-server/${envPath}/${runNumber}`
  window.open(url, '_blank')
}

/* ==========================
   Lifecycle Hooks
========================== */
onMounted(async () => {
  // Load config first to ensure environment data is available
  await configStore.fetchDataEnvironment()
  await Promise.all([fetchAllTestCases(), fetchAllTestCasesWebview()])
  await Promise.all([fetchWorkflowRuns(), fetchWorkflowRunsWebview()])
  fetchAndProcessLatestArtifact()
})
</script>
<style>
.clamp-selection {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
